import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiPencilSimpleBold } from 'react-icons/pi';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

type Props = {
    shipmentId: number;
    value: string | null;
    canEdit: boolean;
};

export default function ShipmentInvoiceCell({ shipmentId, value, canEdit }: Props) {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? '');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!editing) {
            setDraft(value ?? '');
        }
    }, [value, editing]);

    useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing]);

    const mutation = useMutation({
        mutationFn: async (invoice: string | null) => {
            const { data } = await api.patch(`/shipments/${shipmentId}`, { invoice });
            return data;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['shipments'] });
            setEditing(false);
            toast.success('Invoice atualizada');
        },
        onError: () => {
            toast.error('Não foi possível salvar a invoice');
            setDraft(value ?? '');
        },
    });

    const commit = () => {
        const next = draft.trim();
        const current = (value ?? '').trim();
        if (next === current) {
            setEditing(false);
            return;
        }
        mutation.mutate(next === '' ? null : next);
    };

    if (!canEdit) {
        return <span className="whitespace-nowrap text-gray-700 dark:text-zinc-300">{value || '—'}</span>;
    }

    if (editing) {
        return (
            <input
                ref={inputRef}
                type="text"
                value={draft}
                disabled={mutation.isPending}
                maxLength={100}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        commit();
                    }
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        setDraft(value ?? '');
                        setEditing(false);
                    }
                }}
                className={cn(
                    'w-28 rounded-md border border-amber-300 bg-white px-2 py-1 font-mono text-xs text-gray-900 outline-none',
                    'focus:border-amber-500 focus:ring-2 focus:ring-amber-200',
                    'dark:border-amber-500/40 dark:bg-zinc-900 dark:text-zinc-100 dark:focus:ring-amber-900/40',
                    mutation.isPending && 'opacity-60',
                )}
                placeholder="Nº invoice"
                aria-label="Editar número da invoice"
            />
        );
    }

    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                setEditing(true);
            }}
            title="Editar invoice (legado)"
            className={cn(
                'group inline-flex max-w-[9rem] items-center gap-1 rounded-md px-1.5 py-0.5 text-left',
                'hover:bg-amber-50 dark:hover:bg-amber-500/10',
            )}
        >
            <span className="truncate font-mono text-xs font-semibold text-gray-800 dark:text-zinc-200">
                {value || '—'}
            </span>
            <PiPencilSimpleBold className="size-3 shrink-0 text-gray-300 opacity-0 transition group-hover:opacity-100 dark:text-zinc-500" />
        </button>
    );
}
